// src/pages/Admin/Adherents/CreateAdherentAdminModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  FaAddressCard,
  FaBirthdayCake,
  FaCamera,
  FaCheckCircle,
  FaEnvelope,
  FaEuroSign,
  FaHome,
  FaIdCard,
  FaImage,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaNotesMedical,
  FaPhone,
  FaSave,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUser,
  FaUserPlus,
  FaVenusMars,
  FaWeight,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { addNewAdherent } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import { createDossierInscription } from "./../../../services/dossierServices";

const initialState = {
  nom: "",
  prenom: "",
  email: "",
  email_secondaire: "",
  tel: "",
  tel_secondaire: "",
  representant_legal: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "",
  statut_inscription: "Inscrit",
  date_naissance: "",
  lieu_naissance: "",
  photo_url: "",
  licence_number: "",
  poids: "",
  saison_active: "",
  notes_adherent: "",
  autorisation_photo: false,
  passsport_number: "",
  passclub32_number: "",
};

const STATUS_OPTIONS = [
  "Inscrit",
  "Licencié",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Licencié Hors Club",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const normalizeText = (value = "") => String(value || "").trim();

const getPreviousSeason = (currentSeason = "") => {
  const [yearStart, yearEnd] = currentSeason.split("-").map(Number);

  if (!yearStart || !yearEnd) return "";

  return `${yearStart - 1}-${yearEnd - 1}`;
};

const getNextSeason = (currentSeason = "") => {
  const [yearStart, yearEnd] = currentSeason.split("-").map(Number);

  if (!yearStart || !yearEnd) return "";

  return `${yearStart + 1}-${yearEnd + 1}`;
};

const toAbsolute = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (!cleanUrl.startsWith("/uploads")) return cleanUrl;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${cleanUrl}`;
};

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

const formatFullName = (data) => {
  const nom = normalizeText(data?.nom).toUpperCase();
  const prenom = normalizeText(data?.prenom);

  return `${prenom} ${nom}`.trim() || "Nouvel adhérent";
};

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

function PhotoAuthorizationSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className={`flex w-full items-center justify-between gap-4 rounded-2xl p-4 text-left ring-1 transition disabled:cursor-not-allowed disabled:opacity-60 ${
        checked
          ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
          : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:bg-slate-900"
      }`}
    >
      <span className="flex items-center gap-3">
        <span
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
            checked
              ? "bg-emerald-600 text-white"
              : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          }`}
        >
          {checked ? <FaCheckCircle /> : <FaImage />}
        </span>

        <span>
          <span className="block text-sm font-bold">Autorisation photo</span>
          <span className="mt-0.5 block text-xs opacity-75">
            {checked
              ? "La photo peut être affichée sur les supports du club."
              : "La photo ne doit pas être utilisée publiquement."}
          </span>
        </span>
      </span>

      <span
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
      >
        <span
          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </span>
    </button>
  );
}

function AdherentPreviewCard({ formData, previewSrc, imgOk, onImageError }) {
  const fullName = formatFullName(formData);

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaUserPlus className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Création adhérent
            </p>
            <p className="truncate text-lg font-bold">{fullName}</p>
          </div>
        </div>
      </div>

      <div className="p-4 text-center">
        <div className="mx-auto h-40 w-40 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900">
          <img
            src={previewSrc}
            alt={fullName}
            onError={onImageError}
            className="h-full w-full object-cover"
            draggable={false}
          />
        </div>

        {!imgOk && (
          <div className="mt-3 rounded-2xl bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 ring-1 ring-orange-100 dark:bg-orange-950/30 dark:text-orange-300 dark:ring-orange-900">
            Impossible de charger l’image. L’avatar par défaut est utilisé.
          </div>
        )}

        <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
          {fullName}
        </h3>

        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {formData.statut_inscription && (
            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
              {formData.statut_inscription}
            </span>
          )}

          {formData.saison_active && (
            <span className="inline-flex rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:text-violet-200 dark:ring-violet-800/60">
              Saison {formData.saison_active}
            </span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 text-left">
          <PreviewLine label="Email" value={formData.email || "—"} />
          <PreviewLine label="Téléphone" value={formData.tel || "—"} />
          <PreviewLine
            label="Licence"
            value={formData.licence_number || "—"}
          />
        </div>
      </div>
    </section>
  );
}

export default function CreateAdherentAdminModal({
  isOpen,
  onClose,
  refreshList,
}) {
  const dialogRef = useRef(null);

  const [formData, setFormData] = useState(initialState);
  const [seasons, setSeasons] = useState([]);
  const [imgOk, setImgOk] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const loadSeasons = useCallback(async () => {
    try {
      setLoadingSeasons(true);

      const settingsData = await getSettings();
      const active = settingsData?.[0]?.saison_active || "";
      const previous = getPreviousSeason(active);
      const next = getNextSeason(active);
      const options = [previous, active, next].filter(Boolean);

      setSeasons(options);

      setFormData((previousData) => ({
        ...previousData,
        saison_active: previousData.saison_active || active || "",
      }));
    } catch (error) {
      console.error("Erreur chargement settings:", error);
      toast.error("Impossible de charger la saison active.");
    } finally {
      setLoadingSeasons(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      ...initialState,
      saison_active: "",
    });
    setImgOk(true);
    setSubmitting(false);

    loadSeasons();
  }, [isOpen, loadSeasons]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, handleClose]);

  const previewSrc = useMemo(() => {
    if (formData.photo_url && imgOk) {
      return toAbsolute(formData.photo_url);
    }

    return getDefaultPhoto(formData.genre);
  }, [formData.photo_url, formData.genre, imgOk]);

  const canSubmit = useMemo(() => {
    return (
      !submitting &&
      normalizeText(formData.nom).length > 0 &&
      normalizeText(formData.prenom).length > 0 &&
      normalizeText(formData.saison_active).length > 0
    );
  }, [formData.nom, formData.prenom, formData.saison_active, submitting]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "photo_url") {
      setImgOk(true);
    }
  };

  const togglePhotoAuthorization = () => {
    setFormData((previous) => ({
      ...previous,
      autorisation_photo: !previous.autorisation_photo,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      ...formData,
      nom: normalizeText(formData.nom).toUpperCase(),
      prenom: normalizeText(formData.prenom),
      email: normalizeText(formData.email).toLowerCase(),
      email_secondaire: normalizeText(formData.email_secondaire).toLowerCase(),
      tel: normalizeText(formData.tel),
      tel_secondaire: normalizeText(formData.tel_secondaire),
      representant_legal: normalizeText(formData.representant_legal),
      adresse: normalizeText(formData.adresse),
      cp: normalizeText(formData.cp),
      commune: normalizeText(formData.commune),
      lieu_naissance: normalizeText(formData.lieu_naissance),
      photo_url: normalizeText(formData.photo_url),
      licence_number: normalizeText(formData.licence_number),
      passsport_number: normalizeText(formData.passsport_number),
      passclub32_number: normalizeText(formData.passclub32_number),
      notes_adherent: String(formData.notes_adherent || "").trim(),
      poids: formData.poids === "" ? null : formData.poids,
      autorisation_photo: !!formData.autorisation_photo,
    };

    if (!payload.nom) {
      toast.warn("Le nom est obligatoire.");
      return;
    }

    if (!payload.prenom) {
      toast.warn("Le prénom est obligatoire.");
      return;
    }

    if (!payload.saison_active) {
      toast.warn("La saison active est obligatoire.");
      return;
    }

    if (payload.email && !EMAIL_RE.test(payload.email)) {
      toast.warn("L’email principal n’est pas valide.");
      return;
    }

    if (payload.email_secondaire && !EMAIL_RE.test(payload.email_secondaire)) {
      toast.warn("L’email secondaire n’est pas valide.");
      return;
    }

    try {
      setSubmitting(true);

      const newAdherent = await addNewAdherent(payload);

      if (!newAdherent || !newAdherent.id) {
        throw new Error("Création adhérent : pas d'ID retourné.");
      }

      await createDossierInscription(newAdherent.id);

      toast.success("Adhérent + dossier d'inscription créés avec succès.");

      refreshList?.();
      onClose?.();
      setFormData(initialState);
    } catch (error) {
      console.error("Erreur lors de la création adhérent/dossier:", error);
      toast.error("Erreur lors de la création de l'adhérent ou du dossier.");
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Créer un adhérent"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-7xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Gestion adhérents
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                Créer un adhérent
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Créez un adhérent et son dossier d’inscription en une seule
                action. Les champs nom, prénom et saison sont obligatoires.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="create-adherent-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <AdherentPreviewCard
                  formData={formData}
                  previewSrc={previewSrc}
                  imgOk={imgOk}
                  onImageError={() => setImgOk(false)}
                />

                <SectionCard
                  icon={FaCamera}
                  title="Photo"
                  description="Ajoutez une URL de photo si elle est déjà disponible."
                >
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>URL de la photo</label>

                      <div className="relative">
                        <FaImage className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="photo_url"
                          value={formData.photo_url}
                          onChange={handleChange}
                          placeholder="https://… ou /uploads/…"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <PhotoAuthorizationSwitch
                      checked={!!formData.autorisation_photo}
                      onChange={togglePhotoAuthorization}
                      disabled={submitting}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Contrôle rapide avant création."
                >
                  <div className="space-y-3">
                    <PreviewLine
                      label="Nom"
                      value={normalizeText(formData.nom).toUpperCase() || "—"}
                    />
                    <PreviewLine
                      label="Prénom"
                      value={normalizeText(formData.prenom) || "—"}
                    />
                    <PreviewLine
                      label="Saison"
                      value={formData.saison_active || "—"}
                    />
                    <PreviewLine
                      label="Dossier"
                      value="Créé automatiquement"
                    />
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaIdCard}
                  title="État civil"
                  description="Identité principale de l’adhérent."
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>
                        Nom <span className="text-pink-600">*</span>
                      </label>

                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          required
                          placeholder="Nom"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>
                        Prénom <span className="text-pink-600">*</span>
                      </label>

                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          required
                          placeholder="Prénom"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Genre</label>

                      <div className="relative">
                        <FaVenusMars className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <select
                          name="genre"
                          value={formData.genre}
                          onChange={handleChange}
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        >
                          <option value="">— Sélectionner —</option>
                          <option value="Masculin">Masculin</option>
                          <option value="Féminin">Féminin</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Date de naissance</label>

                      <div className="relative">
                        <FaBirthdayCake className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="date"
                          name="date_naissance"
                          value={formData.date_naissance}
                          onChange={handleChange}
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Lieu de naissance</label>

                      <div className="relative">
                        <FaMapMarkerAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="lieu_naissance"
                          value={formData.lieu_naissance}
                          onChange={handleChange}
                          placeholder="Ville de naissance"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Poids</label>

                      <div className="relative">
                        <FaWeight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="number"
                          step="0.1"
                          name="poids"
                          value={formData.poids}
                          onChange={handleChange}
                          placeholder="kg"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaAddressCard}
                  title="Coordonnées"
                  description="Contacts de l’adhérent ou du représentant légal."
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>Email principal</label>

                      <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@exemple.fr"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Email secondaire</label>

                      <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="email"
                          name="email_secondaire"
                          value={formData.email_secondaire}
                          onChange={handleChange}
                          placeholder="email secondaire"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Téléphone</label>

                      <div className="relative">
                        <FaPhone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="tel"
                          value={formData.tel}
                          onChange={handleChange}
                          placeholder="06..."
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Téléphone secondaire</label>

                      <div className="relative">
                        <FaPhone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="tel_secondaire"
                          value={formData.tel_secondaire}
                          onChange={handleChange}
                          placeholder="Téléphone secondaire"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className={labelClass}>Représentant légal</label>

                      <div className="relative">
                        <FaShieldAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="representant_legal"
                          value={formData.representant_legal}
                          onChange={handleChange}
                          placeholder="Nom du parent ou représentant"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className={labelClass}>Adresse</label>

                      <div className="relative">
                        <FaHome className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="adresse"
                          value={formData.adresse}
                          onChange={handleChange}
                          placeholder="Adresse complète"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Code postal</label>

                      <input
                        type="text"
                        name="cp"
                        value={formData.cp}
                        onChange={handleChange}
                        placeholder="Code postal"
                        className={inputClass}
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Commune</label>

                      <input
                        type="text"
                        name="commune"
                        value={formData.commune}
                        onChange={handleChange}
                        placeholder="Commune"
                        className={inputClass}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaNotesMedical}
                  title="Infos club"
                  description="Statut, saison, licence, Pass’Sport et notes internes."
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>Statut d'inscription</label>

                      <select
                        name="statut_inscription"
                        value={formData.statut_inscription}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={submitting}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>
                        Saison active <span className="text-pink-600">*</span>
                      </label>

                      <select
                        name="saison_active"
                        value={formData.saison_active}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        disabled={submitting || loadingSeasons}
                      >
                        <option value="">
                          {loadingSeasons
                            ? "Chargement..."
                            : "— Sélectionner —"}
                        </option>

                        {seasons.map((season) => (
                          <option key={season} value={season}>
                            {season}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Numéro de licence</label>

                      <div className="relative">
                        <FaIdCard className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="licence_number"
                          value={formData.licence_number}
                          onChange={handleChange}
                          placeholder="N° licence"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>N° Pass’Sport</label>

                      <div className="relative">
                        <FaEuroSign className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="passsport_number"
                          value={formData.passsport_number}
                          onChange={handleChange}
                          placeholder="Facultatif"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>N° Pass'Club 32</label>

                      <div className="relative">
                        <FaIdCard className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="passclub32_number"
                          value={formData.passclub32_number}
                          onChange={handleChange}
                          placeholder="Facultatif"
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className={labelClass}>Notes internes</label>

                      <textarea
                        name="notes_adherent"
                        value={formData.notes_adherent}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Notes, informations utiles, suivi..."
                        className={`${inputClass} resize-none leading-relaxed`}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Créer l’adhérent
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .create-adherent-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .create-adherent-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .create-adherent-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .create-adherent-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body
  );
}